import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Calendar, ArrowRight } from 'lucide-react';
import { RedactionText } from '../../redaction/RedactionText';
import { useNavigate } from 'react-router-dom';
import { usePageContext } from '../../../hooks/usePageContext';
import { type PageEvent } from '../../../services/pageContextApi';
import { useAskSidebar } from '../../../contexts/AskSidebarContext';

interface PageSummaryPanelProps {
  documentId: string;
  pageNumber: number;
  caseId?: string;
}

const significanceDotColour: Record<string, string> = {
  MAJOR: 'bg-primary-500',
  SUPPORTING: 'bg-amber-500',
  ROUTINE: 'bg-medical-400',
};

function formatEventDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatEventType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const eventTypePillColour: Record<string, string> = {
  DIAGNOSIS: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  TREATMENT: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  INVESTIGATION: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  REFERRAL: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  CONSULTATION: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  ADMISSION: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  DISCHARGE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  PROCEDURE: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  PRESCRIPTION: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
};

function getEventTypePillColour(eventType: string): string {
  const normalised = eventType.toUpperCase();
  return eventTypePillColour[normalised] ?? 'bg-medical-100 text-medical-600 dark:bg-medical-700 dark:text-medical-300';
}

export const PageSummaryPanel: React.FC<PageSummaryPanelProps> = ({ documentId, pageNumber, caseId }) => {
  const [expanded, setExpanded] = useState(true);
  const { data, isLoading } = usePageContext(documentId, pageNumber);

  const events = data?.events ?? [];

  return (
    <div className="border-t border-medical-200 dark:border-medical-700 bg-medical-50/50 dark:bg-medical-900/30">
      {/* Header */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-medical-600 dark:text-medical-300 hover:text-medical-800 dark:hover:text-medical-100 transition-colors"
      >
        <span>Chronology events on page</span>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-3 pb-2 max-h-[200px] overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-medical-400" />
            </div>
          )}

          {!isLoading && events.length === 0 && (
            <p className="text-[10px] text-medical-400 py-2">
              No chronology events linked to this page
            </p>
          )}

          {!isLoading && events.length > 0 && (
            <div className="space-y-1.5">
              {events.map((event, idx) => (
                <EventCard key={`${event.eventId}-${idx}`} event={event} caseId={caseId} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Event Card ──────────────────────────────────────────────────────────────

interface EventCardProps {
  event: PageEvent;
  caseId?: string;
}

const EventCard: React.FC<EventCardProps> = ({ event, caseId }) => {
  const navigate = useNavigate();
  const { isMaximized, exitMaximize, open } = useAskSidebar();
  const dotColour = significanceDotColour[event.significance] ?? 'bg-medical-400';
  const pillColour = getEventTypePillColour(event.eventType);

  const jumpToChronology = () => {
    if (!caseId) return;
    if (isMaximized) { exitMaximize(); open(); }
    navigate(`/cases/${caseId}/chronology?highlight=${encodeURIComponent(event.eventId)}`);
  };

  return (
    <button
      type="button"
      onClick={jumpToChronology}
      title="View in chronology"
      className="w-full text-left rounded-md border border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800 px-2.5 py-1.5 hover:border-primary-300 hover:bg-primary-50/40 dark:hover:bg-primary-900/20 transition-colors group/card"
    >
      {/* Date + significance dot + event type pill */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Calendar className="h-2.5 w-2.5 text-medical-400 shrink-0" />
        <span className="text-[10px] text-medical-400">
          {formatEventDate(event.eventDate)}
        </span>
        <span className={`h-1.5 w-1.5 rounded-full ${dotColour} shrink-0`} />
        <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded-full ${pillColour}`}>
          {formatEventType(event.eventType)}
        </span>
      </div>

      {/* Headline + jump indicator */}
      <div className="flex items-start justify-between gap-1 mt-1">
        <p className="text-xs font-medium text-medical-700 dark:text-medical-200 leading-snug">
          <RedactionText text={event.headline} />
        </p>
        <ArrowRight className="h-3 w-3 shrink-0 mt-0.5 text-medical-300 group-hover/card:text-primary-500 transition-colors" />
      </div>
    </button>
  );
};
